import { Link } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

/**
 * This file is for finalized copy that has been translated.
 * WIP copy belongs in the `copy/draft.ts` file.
 * Please use consistent naming - the key should be the same as the key in the draft file.
 */

export const settings = translate('Settings');
export const failed = translate('Failed');
export const title = translate('Title');
export const description = translate('Description');
export const tags = translate('Tags');
export const save = translate('Save');
export const replace = translate('Replace');
export const chapters = translate('Chapters');
export const highlight = translate('Highlight');
export const feedback = translate('Thank you for your feedback');
export const upgrade = translate('Upgrade');
export const trial = translate('Start trial');
export const enterprise = translate('Enterprise');
export const startTrial = translate('Start trial to access Vimeo AI');
export const upgradeToAccessAI = translate('Upgrade to access Vimeo AI');
export const play = translate('Play');
export const pause = translate('Pause');
export const fullscreen = translate('Fullscreen');
export const exitFullscreen = translate('Exit Full screen');
export const volumeOn = translate('Unmute');
export const volumeOff = translate('Mute');
export const copied = translate('Copied');
export const copy = translate('Copy');
export const good = translate('Good');
export const bad = translate('Bad');
export const discard = translate('Discard');
export const disabled = translate('Disabled');
export const activationInProgress = translate('Feature activation still in progress');
export const aiUnavailable = () =>
  translate({
    singular:
      'AI is unavailable. We couldn’t detect a transcript for this video. {A}Learn more{/A}',
    replacements: {
      A: (children: React.ReactNode) => (
        <Link
          key="learn-more-link"
          variant={'primary'}
          href="https://help.vimeo.com/hc/en-us/articles/22089012658321-What-are-the-video-requirements-needed-for-Vimeo-AI"
          target="_blank"
        >
          {children}
        </Link>
      ),
    },
  });
export const aiWillBeAvailable = translate(
  'Vimeo AI will be available once your transcript is ready.',
);
export const nonEnglishTranscripts = () =>
  translate({
    singular: 'Non-English transcripts are unlikely to produce accurate results. {A}Learn more{/A}',
    replacements: {
      A: (children: React.ReactNode) => (
        <Link
          key="learn-more-link"
          variant={'primary'}
          href="https://help.vimeo.com/hc/en-us/articles/22089012658321-What-are-the-video-requirements-needed-for-Vimeo-AI"
          target="_blank"
        >
          {children}
        </Link>
      ),
    },
  });

export const upsellModalSubtitle = translate(
  'Speed up your workflow and get more value from your videos.',
);

export const advancedSubHeading = translate('Expand your reach through live events');

export const advancedFeaturesList = [
  translate('AI-generated video details'),
  translate('Auto chapters'),
];

export const generateHighlightPrompts: readonly [string, ...string[]] = [
  translate('Create a 30-second summary of the video using the best moments.'),
  translate('Turn this video into a short recap with the top business takeaways.'),
  translate('Find the strongest quotes and supporting moments for a testimonial clip.'),
  translate('Make a quick teaser that builds excitement for the full video.'),
  translate('Make an executive summary that shows the problem, solution, and results.'),
];

export const askQuestionAction = translate('Ask a question');
export const vimeoAi = translate('Vimeo AI');
export const generatingHighlights = translate('Generating highlights...');
export const videoSummaryFailed = translate('Video details failed');
export const videoSummaryReady = translate('Video details ready');
export const loadingFallbackText = translate('Loading...');
export const generateVideoSummary = translate('Generate video details');
export const generateChapters = translate('Generate chapters');
export const generateHighlight = translate('Generate highlights');
export const generateHighlightReel = translate('Generate highlight reel');
export const highlightAskForEdits = translate('Ask for edits');
export const clearChat = translate('Clear chat');

// Highlight reel chat status messages
export const statusMessageAnalyzingVideo = translate('Analyzing video');
export const statusMessageCreatingHighlight = translate('Creating highlight');
export const statusMessageProcessingHighlight = translate('Processing highlight');
export const statusMessageHighlightReady = translate('Your highlight reel is ready');

// Highlight reel chat error messages
export const errorMessageFailedToCreate = translate('Failed to create highlight reel');
export const errorMessageFailedToGenerate = translate('Failed to generate highlight reel');
export const errorMessageNoResult = translate('Highlight reel completed but no result available');
export const errorMessageNetwork = translate('Network error, please try again');
export const generateQnA = translate('Generate Q&A');
export const activatingAi = translate(
  'Generating · This may take a few minutes · You can leave this page',
);
export const activatingAiLong = translate('Generating · Still working · You can leave this page');

export const activatingAiShort = translate('Activating AI...');
export const aiIsAnalyzing = translate(
  'Analyzing this video for key insights. This may take a few minutes...',
);
export const chaptersSavedSuccessfully = (count: number, total: number) =>
  translate({
    singular: '{COUNT} of {TOTAL} chapter saved',
    plural: '{COUNT} of {TOTAL} chapters saved',
    count: total,
    replacements: {
      COUNT: count,
      TOTAL: total,
    },
  });

export const saveAsNewVideo = translate('Save as a new video');
export const summarySectionTitle = translate('Video details');
export const questionSectionTitle = translate('Answer');
export const chaptersSectionTitle = translate('Chapters');
export const qnaSectionTitle = translate('Q&A');
export const highlightsSectionTitle = translate('Video highlights');
export const highlightReelSectionTitle = translate('Generate highlight reel');
export const defaultQnAQuestion = translate('What is this video about?');
export const back = translate('Back');
export const videoHighlightsWithCount = (count: number) =>
  translate({
    singular: '{COUNT} moment',
    plural: '{COUNT} moments',
    count,
    replacements: {
      COUNT: count,
    },
  });
export const chaptersWithCount = (count: number) =>
  translate({
    singular: '{COUNT} chapter',
    plural: '{COUNT} chapters',
    count,
    replacements: {
      COUNT: count,
    },
  });
export const playMoment = translate('Play moment');
export const aiUnavailableTranscriptTooShort = () =>
  translate({
    singular: 'AI is unavailable. The transcript for this video is too short. {A}Learn more{/A}',
    replacements: {
      A: (children: React.ReactNode) => (
        <Link
          key="learn-more-link"
          variant={'primary'}
          href="https://help.vimeo.com/hc/en-us/articles/22089012658321-What-are-the-video-requirements-needed-for-Vimeo-AI"
          target="_blank"
        >
          {children}
        </Link>
      ),
    },
  });
export const changesSaved = translate('Changes saved');
export const recap = translate('Recap');
export const answer = translate('Answer');
export const generatingAnswer = translate('Generating answer...');
export const failedToAskTheVimeoAi = translate('Failed to ask the Vimeo AI');
export const askAboutThisVideo = translate('Ask about this video');
export const failedToSave = translate('Failed to save');
export const generateQnaForViewers = translate('Generate Q&A for viewers');
export const copyLink = translate('Copy link');
export const related = translate('Related');
export const errorOccurred = translate('An error occured');
export const refresh = translate('Refresh');
export const youMustBeLoggedIn = translate('You must be logged in to use Ask Vimeo AI');
export const logIn = translate('Log in');
export const translateVideo = translate('Translate video');
export const createSubtitleAndSpokenTranslations = translate(
  'Create subtitles and spoken translations using the voices in this video',
);
export const selectLanguages = translate('Select languages');
export const translateXSecPreview = (seconds: number) =>
  translate({
    singular: 'Translate {SECONDS} seconds preview',
    replacements: {
      SECONDS: seconds,
    },
  });
export const speakers = (speakersCount: number) =>
  translate({
    singular: '{SPEACKERS} speaker',
    plural: '{SPEACKERS} speakers',
    count: speakersCount,
    replacements: {
      SPEACKERS: speakersCount,
    },
  });
export const autoDetect = translate('Auto-detect number of speakers');
export const generatePreview = translate('Generate free preview');
export const translationsDisclaimer = translate(
  "By continuing, you confirm you've met all legal requirements to use the voices of the people in this video to create synthetic audio. That may include getting written consent.",
);
export const continueText = translate('Continue');

export const aiTranslationsUnavailableLongVideo = translate(
  'AI translation is unavailable for videos longer than 45 minutes',
);

export const aiTranslationsUnavailableLongVideoExt = translate(
  'AI translation is unavailable for videos longer than 150 minutes',
);
export const translationsPreviewTooltipText = translate(
  'Preview may differ slightly from final translation',
);
export const ccSubtitles = translate('CC/Subtitles');
export const aiTranslationUnsupportedLanguage = translate(
  'AI translation from the original language of this video is unsupported',
);
export const translationsFinishAndSave = translate('Finish and save');
export const translationsSubtitlesAndAudioDubs = (count: number) =>
  translate({
    singular: '1 language',
    plural: '{COUNT} languages',
    count,
    replacements: { COUNT: count },
  });
export const translationsTokensNotEnough = translate('Not enough translation minutes available');
export const translationsTokensRemaining = (count: number) =>
  translate({
    singular: '{COUNT} translation minute left',
    plural: '{COUNT} translation minutes left',
    count,
    replacements: { COUNT: count.toLocaleString('en-Us') },
  });
export const translationsUseTokens = (count: number) =>
  translate({
    singular: 'Use {COUNT} min',
    replacements: { COUNT: count },
  });
export const activatingTranslation = (time: string) =>
  translate({
    singular: 'Translating · This may take up to {TIME} min · You can leave this page',
    replacements: {
      TIME: time,
    },
  });
export const activatingTranslationNoEstimate = translate(
  'Estimating translation time · You can leave this page',
);
export const translationTakingLongerThanExpected = translate(
  'Translating  ·  This translation is taking longer than expected  ·  You can leave this page',
);
export const activatingTranslationSubtitling = translate('Translating · You can leave this page');
export const startOver = translate('Start over');
export const chooseAQuestion = translate('Choose a question from the list or ask your own');

export const stillYourLocation = (currentLocationString: string) =>
  translate({
    singular: 'Do you still reside in {CURRENT_LOCATION}?',
    replacements: {
      CURRENT_LOCATION: currentLocationString,
    },
  });
export const learnAboutRequirements = translate('Why do we need this?');
export const locationPlaceholder = translate('Select location');
export const locationTitle = translate('Where do you reside?');
export const locationError = translate('Invalid Location');
export const restrictionMessage = translate(
  'This feature is not currently available to residents of your state',
);
export const updateProfile = translate('Update location');
export const yes = translate('Yes');
export const no = translate('No');
export const cont = translate('Continue');
export const translationSaved = translate('Translation saved');
export const aiCanBeWrong = translate('AI doesn’t always get it right. Double-check results.');
export const whatWouldYouLikeToDo = translate('What would you like to do?');
export const qnaTermAndConditions = () =>
  translate({
    singular:
      'By using Vimeo AI, you agree to our {TERMS}Terms of Service{/TERMS} and acknowledge our {PRIVACY}Privacy Policy{/PRIVACY}.',
    replacements: {
      TERMS: (children: React.ReactNode) => (
        <Link
          key="terms-of-service-link"
          variant={'inline-secondary'}
          href="https://vimeo.com/terms"
          target="_blank"
          fontSize="inherit"
        >
          {children}
        </Link>
      ),
      PRIVACY: (children: React.ReactNode) => (
        <Link
          key="privacy-policy-link"
          variant={'inline-secondary'}
          href="https://vimeo.com/privacy"
          target="_blank"
          fontSize="inherit"
        >
          {children}
        </Link>
      ),
    },
  });
export const unavailableUntilTranscriptIsReady = translate('Unavailable until transcript is ready');
export const transcriptIsTooShort = translate('Transcript is too short');
export const unavailableForThisVideosLanguage = translate("Unavailable for this video's language");
export const transcriptNotDetected = translate('Transcript not detected');
export const translationsUnavailableWhileTranscoding = translate(
  'Translations will be available once your transcoding is ready',
);
export const translationTryAgain = translate(
  'Unable to process this translation. Please try again.',
);
export const unsupportedSourceLanguage = translate(
  "AI translation is not supported for this video's language",
);
export const languageAlreadyTranslated = translate('Language already translated');
export const tryVimeoAi = translate('Try Vimeo AI');
export const discardAll = translate('Discard all');
export const subtitlesOnly = translate('Subtitles only');
export const audioAndSubtitles = translate('Audio and subtitles');
export const createSubtitlesUsingTranscript = translate(
  'Create subtitles using this video’s transcript',
);

export const replaceExistingTranslation = translate('Replace existing translation');
export const replaceExistingTranslations = translate('Replace existing translations');
export const alreadyExistingTranslationsWarning = translate(
  'There are already translations of this video in the following languages. Do you want to replace them?',
);
export const alreadyExistingTranslationWarning = (language: string) => {
  return translate({
    singular:
      'There’s already a translation of this video in {LANGUAGE}. Do you want to replace it?',
    replacements: {
      LANGUAGE: language,
    },
  });
};

export const cancelTranslation = translate('Cancel translation');
export const translateTo = translate('Translate to');
export const translateFrom = translate('Translate from');
export const original = translate('original');
export const allTranslationsFailedErrorMessage = translate(
  'Unable to translate this video right now, so your credits have been returned.',
);
export const someTranslationsFailedErrorMessage = (
  languagesList: string[],
  tokensReturned: number,
) => {
  return translate({
    singular:
      'Unable to translate this video into {LANGUAGES_LIST}, so {TOKENS_AMOUNT} credits have been returned',
    replacements: {
      LANGUAGES_LIST: languagesList.join(', '),
      TOKENS_AMOUNT: tokensReturned,
    },
  });
};
export const tryAgain = translate('Try again');

export const creditsPerMin = (credits: number) =>
  translate({
    singular: '{CREDITS} per min',
    replacements: {
      CREDITS: credits,
    },
  });
export const useCredits = (credits: number) =>
  translate({
    count: credits,
    singular: '{CREDITS} credit',
    plural: '{CREDITS} credits',
    replacements: {
      CREDITS: credits,
    },
  });
export const creditsLeft = (credits: number) =>
  translate({
    count: credits,
    singular: '{CREDITS} credit remaining',
    plural: '{CREDITS} credits remaining',
    replacements: {
      CREDITS: credits.toLocaleString('en-Us'),
    },
  });
export const contactSales = translate('Contact sales');
export const ok = translate('Ok');
export const contactVimeoToBuyCredits = translate('Contact Vimeo to buy more AI credits');
export const reachOutToYourVimeoAccountOwner = translate(
  'Reach out to your Vimeo account owner to purchase more',
);
export const toPurchaseMoreAICreditsMailto = () =>
  translate({
    singular:
      'To purchase more AI credits, please reach out to your Account Manager or email {A}sales@vimeo.com{/A}.',
    replacements: {
      A: (children: React.ReactNode) => (
        <Link
          key="sales-email-link"
          href="mailto:sales@vimeo.com"
          target="_blank"
          fontSize={'inherit'}
        >
          {children}
        </Link>
      ),
    },
  });
export const creditsNeeded = (credits: number) =>
  translate({
    singular: 'You need {CREDITS} more credits to complete this translation.',
    replacements: {
      CREDITS: credits,
    },
  });

export const unlockAdvancedAIVideoTools = translate('Unlock advanced AI video tools');
export const plusFeaturesList = [
  translate('250GB total storage'),
  translate('Subtitle translations (with AI credits)'),
];
export const proFeaturesList = [
  translate('1TB total storage'),
  translate('Text-based video editing'),
  translate('AI script generator'),
  translate('Teleprompter'),
];
export const premiumSubHeader = translate('Drive engagement with Vimeo AI and events');
export const premiumFeaturesList = [
  translate('7TB total storage'),
  translate('AI-generated video details'),
  translate('Auto chapters'),
];
export const legacyEnterpriseFeaturesList = [
  translate('More storage and bandwidth'),
  translate('Audio translations (with AI credits)'),
  translate('AI-generated highlight reels'),
  translate('Ask AI'),
];
export const starterFeaturesList = [translate('Subtitle translations (with AI credits)')];
export const standardFeaturesList = [
  translate('Text-based video editing'),
  translate('AI script generator'),
  translate('Teleprompter'),
];
export const enterpriseFeaturesList = [
  translate('Audio translations (with AI credits)'),
  translate('AI-generated highlight reels'),
  translate('Ask AI'),
];
export const unlockAITools = translate('Unlock AI tools');
export const addCredits = translate('Add credits');

export const translateFromTypes = {
  captions: translate('Closed Captions'),
  subtitles: translate('Subtitles'),
};
export const ai = translate('AI');
export const selectTranscript = translate('Select transcript');

export const someFeaturesUnavailableWhileTranscribing = translate(
  'Some features are unavailable while video is being transcribed',
);

export const detectedAudioUnSupported = (language: string, onLinkClick: () => void) =>
  translate({
    singular:
      'The detected audio is in {LANGUAGE}, which isn’t supported for these AI features. If this audio isn’t {LANGUAGE}, {A}re-transcribe the video{/A}.',
    replacements: {
      LANGUAGE: language,
      A: (children: React.ReactNode) => (
        <Link
          fontSize={'inherit'}
          variant={'inline-primary'}
          cursor={'pointer'}
          onClick={onLinkClick}
        >
          {children}
        </Link>
      ),
    },
  });

export const videoCantBeTranscribed = (language: string, onLinkClick: () => void) =>
  translate({
    singular:
      'Videos in {LANGUAGE} can’t be transcribed. If this audio isn’t {LANGUAGE}, try {A}transcribing the video again{/A}.',
    replacements: {
      LANGUAGE: language,
      A: (children: React.ReactNode) => (
        <Link
          fontSize={'inherit'}
          variant={'inline-primary'}
          cursor={'pointer'}
          onClick={onLinkClick}
        >
          {children}
        </Link>
      ),
    },
  });

export const noSpeechWasDetected = (onLinkClick: () => void) =>
  translate({
    singular:
      'Some AI features require a transcript. No speech was detected in this video, so no transcript was generated. {A}Try transcribing again{/A}.',
    replacements: {
      A: (children: React.ReactNode) => (
        <Link
          fontSize={'inherit'}
          variant={'inline-primary'}
          cursor={'pointer'}
          onClick={onLinkClick}
        >
          {children}
        </Link>
      ),
    },
  });

export const uploadFile = translate('Upload file');

export const cloneSpeakersVoice = translate('Clone speaker’s voice');
export const cloneSpeakersVoiceTooltip = translate(
  'Check to clone the speakers’ voice and accent or uncheck to use a native library voice',
);
export const aiVoices = translate('AI voices');
export const selectLanguage = translate('Select language');
export const remove = translate('Remove');

export const auto = translate('Auto');
export const numberOfSpeakers = translate('Number of speakers');

export const translateCopy = translate('Translate');
export const forCopy = translate(' for ');
export const aiVoicesTooltipText = translate(
  'Use a library voice that matches the selected language and accent.',
);

export const thisLanguageAlreadyAdded = translate('This language has already been added');

export const unsupportedResolutionForHighlights = translate(
  'Unavailable for videos lower than 360p resolution',
);

export const translateFor = translate('Translate for');
export const previewTranslations = translate('Preview translations');

export const disabledFeatureGroupTooltip = (feature: string) => {
  switch (feature) {
    case 'translation':
      return translate('Translations have been disabled.\nContact your admin for access.');
    case 'summary':
      return translate('Video details have been disabled.\nContact your admin for access.');
    case 'highlights':
      return translate('Highlights have been disabled.\nContact your admin for access.');
    case 'chapters':
      return translate('Chapters have been disabled.\nContact your admin for access.');
    case 'ask':
      return translate('Ask about this video has been disabled.\nContact your admin for access.');
  }
};
